import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FiUser, 
  FiClock,
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiAlertTriangle,
  FiBookOpen,
  FiUsers,
  FiServer,
  FiDollarSign,
  FiTrendingUp
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import logo from '../../../assets/T.png';

const BlogWhyERPsFail = () => {
  const navigate = useNavigate();
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  // SEO structured data
  useEffect(() => {
    document.title = "Why Most School ERPs Fail in India: 6 Ground Reality Mismatches | Truyon";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = "Discover why 70% of school ERPs fail in India. Learn the 6 critical ground reality mismatches and how Truyon is built differently for Indian schools.";
    }

    // Schema markup
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Why Most School ERPs Fail in India: 6 Ground Reality Mismatches",
      "description": "Discover why 70% of school ERPs fail in India. Learn the 6 critical ground reality mismatches and how Truyon is built differently for Indian schools.",
      "author": {
        "@type": "Organization",
        "name": "Truyon"
      },
      "datePublished": "2026-08-13",
      "dateModified": "2026-08-13"
    });
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const failureReasons = [
    {
      id: 'internet',
      icon: <FiServer className="w-6 h-6 text-indigo-600" />,
      title: 'Internet Dependency Crisis',
      description: 'Most ERPs are designed assuming high-speed, always-on internet. In Indian schools, especially in tier-2 and tier-3 cities, connectivity is often patchy. Teachers at a school in Bihar reported spending 45 minutes just to log in.',
      stat: '60% of Indian schools face internet connectivity issues daily',
      solution: 'Truyon works offline-first, syncing data when connectivity is restored.',
      color: 'bg-indigo-50'
    },
    {
      id: 'language',
      icon: <FiUsers className="w-6 h-6 text-emerald-600" />,
      title: 'English-Only Language Barrier',
      description: 'Many teachers and administrators in Indian schools are comfortable with Hindi or regional languages. Yet, most ERP interfaces remain stubbornly English-only, forcing staff to navigate complex screens in a language they are not fluent in.',
      stat: '80% of school staff prefer regional language interfaces',
      solution: 'Truyon supports multiple Indian languages with complete interface translation.',
      color: 'bg-emerald-50'
    },
    {
      id: 'mobile',
      icon: <FiTrendingUp className="w-6 h-6 text-amber-600" />,
      title: 'Desktop-First Assumption',
      description: 'In Indian reality, teachers and administrators often use mobile phones as their primary computing device. ERPs built for large desktop screens become unusable on 6-inch displays, leading to low adoption rates.',
      stat: '70% of teachers access school systems via mobile phones',
      solution: 'Truyon is mobile-first, providing full functionality on smartphones.',
      color: 'bg-amber-50'
    },
    {
      id: 'complexity',
      icon: <FiBookOpen className="w-6 h-6 text-rose-600" />,
      title: 'Unnecessary Complexity Overload',
      description: 'ERP developers often pack every possible feature, forgetting that most schools only need 20% of the functionality. Teachers spend days in training and still struggle with basic operations, leading to frustration and system abandonment.',
      stat: 'Average school uses only 15% of ERP features actively',
      solution: 'Truyon focuses on simplicity with a clean, intuitive interface for daily tasks.',
      color: 'bg-rose-50'
    },
    {
      id: 'cost',
      icon: <FiDollarSign className="w-6 h-6 text-blue-600" />,
      title: 'Hidden Costs and Budget Burden',
      description: 'The initial licensing fee is just the beginning. Indian schools face additional costs for training, data migration, customization, support, and infrastructure upgrades. These hidden costs often exceed the original budget, putting strain on school finances.',
      stat: 'Schools spend 3x the initial ERP cost on hidden expenses',
      solution: 'Truyon offers transparent, all-inclusive pricing with zero hidden costs.',
      color: 'bg-blue-50'
    },
    {
      id: 'support',
      icon: <FiClock className="w-6 h-6 text-purple-600" />,
      title: 'Delayed Support and Maintenance',
      description: 'Most ERP vendors provide support during standard business hours in a different timezone. When a school in India has an urgent issue during peak activity hours, support often arrives the next day, leaving staff paralyzed.',
      stat: '40% of schools wait over 24 hours for ERP support response',
      solution: 'Truyon provides Indian timezone support with response times under 2 hours.',
      color: 'bg-purple-50'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={logo} alt="Truyon Logo" className="w-8 h-8 object-contain" />
            <span className="text-xl font-bold tracking-tight text-slate-900">truyon</span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsDemoModalOpen(true)}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Request Demo
            </button>
            <Link to="/" className="text-slate-600 hover:text-slate-900 p-2 rounded-lg hover:bg-slate-100 transition-colors">
              <FiArrowLeft className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        
        {/* Blog Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-indigo-600 font-semibold mb-3">
            <span className="bg-indigo-50 px-3 py-1 rounded-full">Blog</span>
            <span className="text-slate-400">•</span>
            <span className="text-slate-500">August 13, 2026</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight">
            Why Most School ERPs Fail in India: 6 Ground Reality Mismatches
          </h1>
          
          <p className="mt-4 text-lg sm:text-xl text-slate-600">
            Discover why 70% of school ERPs fail to deliver value and how Indian schools can make better choices.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <FiUser className="w-4 h-4" />
              <span>By Truyon Team</span>
            </div>
            <div className="flex items-center gap-2">
              <FiClock className="w-4 h-4" />
              <span>8 min read</span>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-4">Table of Contents</h2>
          <ul className="space-y-2 text-sm">
            {failureReasons.map((reason, index) => (
              <li key={index}>
                <button
                  onClick={() => scrollToSection(reason.id)}
                  className="text-slate-600 hover:text-indigo-600 hover:underline flex items-center gap-2 w-full text-left"
                >
                  <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {index + 1}
                  </span>
                  {reason.title}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Introduction */}
        <div className="prose prose-slate max-w-none mb-8">
          <p className="text-lg text-slate-600 leading-relaxed">
            The Indian school education sector is one of the largest in the world, with over 1.5 million schools and 260 million students. Yet, when it comes to digital transformation, the adoption of school management software remains surprisingly low.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed">
            Research suggests that nearly <strong className="text-slate-900">70% of school ERP implementations fail</strong> to achieve their intended objectives. Schools invest substantial resources only to find themselves stuck with complex, underutilized systems that create more problems than they solve.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed">
            Why does this happen? The answer lies not in the technology itself, but in a fundamental disconnect between what ERPs offer and what Indian schools actually need. Here are the <strong className="text-slate-900">6 ground reality mismatches</strong> that cause most school ERPs to fail.
          </p>
        </div>

        {/* Stats Highlight */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <div className="text-3xl font-extrabold text-indigo-600">70%</div>
            <div className="text-sm text-slate-500">ERP implementations fail</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <div className="text-3xl font-extrabold text-amber-600">60%</div>
            <div className="text-sm text-slate-500">Schools face connectivity issues</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <div className="text-3xl font-extrabold text-rose-600">3x</div>
            <div className="text-sm text-slate-500">Hidden costs beyond initial price</div>
          </div>
        </div>

        {/* Failure Reasons */}
        <div className="space-y-12">
          {failureReasons.map((reason, index) => (
            <motion.div
              key={index}
              id={reason.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`${reason.color} rounded-2xl p-6 sm:p-8 border border-slate-200/60`}
            >
              <div className="flex items-start gap-4">
                <div className="bg-white rounded-xl p-3 shadow-sm">
                  {reason.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                    <span className="bg-white px-2.5 py-0.5 rounded-full text-xs font-bold">Mismatch {index + 1}</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{reason.title}</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">{reason.description}</p>
                  
                  <div className="bg-white/60 rounded-xl p-4 mb-4 border border-slate-200/40">
                    <div className="flex items-center gap-2 text-sm font-semibold text-rose-600 mb-1">
                      <FiAlertTriangle className="w-4 h-4" />
                      <span>The Reality</span>
                    </div>
                    <p className="text-sm text-slate-700">{reason.stat}</p>
                  </div>

                  <div className="bg-white rounded-xl p-4 border border-emerald-200/60">
                    <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                      <FiCheckCircle className="w-4 h-4" />
                      <span>Truyon's Approach</span>
                    </div>
                    <p className="text-sm text-slate-700">{reason.solution}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Conclusion */}
        <div className="mt-12 bg-indigo-600 rounded-2xl p-8 text-white">
          <div className="max-w-2xl">
            <h2 className="text-2xl font-bold mb-3">The Bottom Line</h2>
            <p className="text-indigo-100 leading-relaxed">
              The failure of most school ERPs in India is not due to a lack of technology, but a lack of understanding of the Indian school ecosystem. Success requires software that works with the ground reality, not against it.
            </p>
            <p className="text-indigo-100 leading-relaxed mt-3">
              That's why Truyon is built differently—for Indian schools, by people who understand Indian education.
            </p>
            <button
              onClick={() => setIsDemoModalOpen(true)}
              className="mt-6 px-8 py-3 bg-white text-indigo-600 font-bold rounded-xl hover:bg-indigo-50 transition-colors inline-flex items-center gap-2"
            >
              Request a Demo <FiArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Author Bio */}
        <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 flex items-start gap-4">
          <div className="w-12 h-12 bg-indigo-100 text-indigo-700 rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0">
            T
          </div>
          <div>
            <h4 className="font-bold text-slate-900">Truyon Education Team</h4>
            <p className="text-sm text-slate-500">We help Indian schools embrace digital transformation with software that actually works in the real world.</p>
          </div>
        </div>
      </main>

      {/* Demo Request Modal */}
      {isDemoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-900">Request a Demo</h3>
              <button
                onClick={() => setIsDemoModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100"
              >
                <FiArrowLeft className="w-5 h-5" />
              </button>
            </div>
            <p className="text-slate-600 text-sm mb-6">
              See how Truyon solves the 6 mismatches in real action. Our team will show you a personalized walkthrough.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setIsDemoModalOpen(false);
                  navigate('/request-demo');
                }}
                className="w-full py-3 px-6 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold"
              >
                Book Your Demo Now
              </button>
              <button
                onClick={() => setIsDemoModalOpen(false)}
                className="w-full py-2.5 px-4 text-sm text-slate-500 hover:text-slate-700"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogWhyERPsFail;